import CollectionApi from 'client/api/v2/CollectionApi';
import LocalStorageService from 'client/util/LocalStorageService';
import LogoutHandler from 'client/util/LogoutHandler';
import MspState from 'components/models/MspState';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import axios from 'axios';

const apiV2Route = '/api/v2/organizations';
class OrganizationsApi extends CollectionApi {
  /**
   * @description Set up the organizations api for multi-tenant organizations
   */
  constructor() {
    super('/api/organizations', {
      limit: 50,
      skip: 0,
      sortIgnoreCase: 'settings.name',
      search: {
        fields: ['settings.name'],
        searchTerm: '',
      },
    });
  }

  /**
   * @description Make a call to the /api/organizations endpoint to get the orgs associated
   * with this user.  The providerId is now required for this endpoint.
   * @returns An array of results from the Organizations endpoint:
   * [
   *   {
   *     _id: 'ORG_ID',
   *     id: 'ORG_ID',
   *     created: 'yyyy-mm-dd',
   *     displayName: 'Org Name',
   *     logoUrl: '/this/is/a/logo',
   *   },
   *   ...
   * ]
   *
   */
  getOrganizations() {
    // Construct the url with the sort parameter
    const url = `${this.baseUrl}?sort=${this.defaultParams.sort}`;
    return this.getAllItems(url);
  }

  /**
   * @description Call the /api/organizations/:id endpoint with fields query-params
   * to get the necessary org details.
   * @param {string} id - Organization id
   *
   * @todo Eventually this data could also include Directory states (e.g. Google expiry date)
   * and/or other detail information.
   *
   * @see https://github.com/TheJumpCloud/jumpcloud-issues/issues/2967
   */
  getOrganizationDetails(id, fields = '') {
    return this.initialize()
      .then(() => axios.get(`${this.baseUrl}/${id}?fields=${fields}`).then(resp => resp.data));
  }

  /**
   * @description For a given organization, get the logoUrl if it exists.
   * @param {string} id - Organization id
   */
  getOrganizationLogoUrl(id) {
    return this.initialize()
      .then(() => axios.get(`${this.baseUrl}/${id}`).then(resp => resp.data.logoUrl));
  }

  /**
   * @param {string} id - Organization id
   */
  getPaymentDetails(id) {
    return this.initialize()
      .then(() => axios.get(`${this.baseUrl}/${id}/paymentdetails`).then(resp => resp.data));
  }

  /**
   * @param {string} id - Organization id
   */
  getPaymentParameters(id) {
    return this.initialize()
      .then(() => axios.get(`${this.baseUrl}/${id}/paymentparams`).then(resp => resp.data));
  }

  /**
   * @description For a given organization, save org settings information from
   * the Settings panel tabs.
   * @param {Object} settings - Organization settings object - must include id
   */

  update(settings) {
    return this.updateOrganizationSettings(settings.id, settings);
  }

  /**
   * @description For a given organization, save org settings information from
   * the Settings panel tabs.
   * @param {string} id - Organization id
   * @param {Object} settings - Settings JSON object
   */
  updateOrganizationSettings(id, settings) {
    return this.initialize()
      .then(() => axios.put(`${this.baseUrl}/${id}`, settings));
  }

  /**
   * @param {string} id - Organization id
   * @param {Object} payload - JSON Object containing payment details to update
   */
  updatePaymentDetails(id, payload) {
    return this.initialize()
      .then(() => axios.put(`${this.baseUrl}/${id}/paymentdetails`, payload));
  }

  /**
   * @description Submits a deletion request for the given organization.
   */
  requestDeleteOrganization(id) {
    return this.initialize()
      .then(() => axios.delete(`${this.baseUrl}/${id}`));
  }

  /**
   * @description Immediately deletes organization and all its data.
   */
  autoDeleteOrganization({
    id, name, deleteKey, isProviderAdmin, redirectTarget, resendOrgDeleteKey,
  }) {
    if (!deleteKey) {
      return this.initialize()
        .then(() => axios.delete(`${this.baseUrl}/${id}`));
    }
    if (resendOrgDeleteKey) {
      return this.initialize()
        .then(() => axios.delete(`${this.baseUrl}/${id}`, { params: { resendOrgDeleteKey } }));
    }
    return this.initialize()
      .then(() => axios.delete(`${this.baseUrl}/${id}`, { headers: { 'x-org-delete-key': deleteKey } }))
      .then((res) => {
        if (res.data.deauthenticate) {
          if (isProviderAdmin) {
            // cleanup required for redirect to MTP
            MspState.clearStorage();

            // add item in local storage to trigger notification in MTP
            LocalStorageService.setItem('childOrgRecentlyDeleted', name);
          } else {
            // clear cache and localStorage
            LogoutHandler.handleCleanup();

            // add item in local storage to trigger banner on signup page
            LocalStorageService.setItem('orgDeletedDate', new Date().getTime());
          }
          // redirect to /signup or /mtp/organizations depending on admin type
          PageNavigationUtil.setLocation(redirectTarget);
        }
        return res;
      });
  }

  /**
   * @param {string} orgId - Organization id
   * @param {string} productCode - Product code to subscribe this org to
   */
  addSubscription({
    committedUsers,
    components,
    contractType,
    orgId,
    productCode,
  }) {
    return this.initialize()
      .then(() => axios.put(`${this.baseUrl}/${orgId}/subscription`, {
        committedUsers,
        components,
        contractType,
        planProductCode: productCode,
      }));
  }

  /**
   * @param {string} orgId - Organization id
   * @param {Object} payload - JSON Object containing command template details to create
   */
  createCommandTemplate(id, payload) {
    return this.initialize()
      .then(() => axios.post(`${apiV2Route}/${id}/commandtemplates`, payload));
  }

  getCommandTemplate(orgId, idCommandtemplate) {
    return this.initialize()
      .then(() => axios.get(`${apiV2Route}/${orgId}/commandtemplates/${idCommandtemplate}`));
  }

  createPolicyGroupFromTemplate(payload, orgId) {
    return this.initialize()
      .then(() => axios.post(`${apiV2Route}/${orgId}/policygroups/fromTemplate`, payload));
  }
}

export default new OrganizationsApi();
